import { useTranslation } from 'react-i18next';

import { Badge } from '@/components/atoms/Badge';
import { cardSurface } from '@/components/atoms/Card';
import Checkbox from '@/components/atoms/Checkbox';
import EmptyState from '@/components/molecules/EmptyState';
import { AllocationSummary } from '@/components/organisms/FishGroupForms';
import type { FishGroupPopulationInfo, OverviewCage } from '@/domain/fishGroups';

import { computeAllocationSummary } from '../../operateWizardHelpers';
import type {
    OperateWizardAction,
    WizardDestination,
    WizardSource,
} from '../../operateWizardReducer';

import styles from '../../operateModals.module.scss';

interface Props {
    /** All cages in the operation's location (active + inactive). */
    locationCages: OverviewCage[];
    /** Sources currently selected in wizard state. */
    selectedSources: WizardSource[];
    /** The fish group that opened the wizard — flagged with a pill. */
    initialFishGroupId: number;
    destinations: WizardDestination[];
    populationById: Map<number, FishGroupPopulationInfo>;
    dispatch: (action: OperateWizardAction) => void;
}

/**
 * Step 1 of the operate wizard. Lists every active fish group in the
 * operation's location as a checkbox row. The fish group the operator
 * opened the wizard from is pre-selected (handled by the reducer's initial
 * state) and tagged with an "Initial" pill so the operator knows where the
 * flow started.
 *
 * Cross-location candidates are filtered out at the parent level — this
 * component never sees them — so the list shown here is already
 * intra-location by construction.
 */
export default function SourcesStep(props: Props) {
    const {
        locationCages,
        selectedSources,
        initialFishGroupId,
        destinations,
        populationById,
        dispatch,
    } = props;
    const { t } = useTranslation('fishGroups');

    const allocationSummary = computeAllocationSummary({
        sources: selectedSources,
        destinations,
        populationById,
    });

    // Build the candidate list: every cage in the location that currently
    // holds an active fish group. We use the cage's name to give the operator
    // context — two FGs called "Group 12" in different cages are otherwise
    // indistinguishable.
    const candidates = locationCages
        .filter((cage) => cage.activeFishGroup !== null)
        .map((cage) => ({
            fishGroupId: cage.activeFishGroup?.id ?? 0,
            name: cage.activeFishGroup?.name ?? '',
            cageId: cage.cageId,
            cageName: cage.cageName,
        }));

    const selectedIds = new Set(selectedSources.map((s) => s.fishGroupId));

    return (
        <section>
            <h4 className={styles.heading}>{t('actions.operate.sources.heading')}</h4>
            <p className={styles.description}>{t('actions.operate.sources.description')}</p>

            <AllocationSummary
                summary={allocationSummary}
                sources={selectedSources}
                populationById={populationById}
                titleKey="actions.operate.allocation.combinedSourcesTitle"
            />

            {candidates.length === 0 ? (
                <EmptyState title={t('actions.operate.sources.empty')} appearance="quiet" />
            ) : (
                <ul className={styles.sourcesList}>
                    {candidates.map((candidate) => {
                        const isSelected = selectedIds.has(candidate.fishGroupId);
                        const isInitial = candidate.fishGroupId === initialFishGroupId;
                        return (
                            <li
                                key={candidate.fishGroupId}
                                {...cardSurface(
                                    { radius: 'sm', padding: 'none', interactive: true },
                                    styles.sourceRow
                                )}
                                onClick={() =>
                                    dispatch({
                                        type: 'toggleSource',
                                        source: {
                                            fishGroupId: candidate.fishGroupId,
                                            name: candidate.name,
                                            currentCageId: candidate.cageId,
                                        },
                                    })
                                }>
                                <Checkbox
                                    checked={isSelected}
                                    // The actual toggle happens on row click so
                                    // the whole row is the hit target; the
                                    // checkbox just reflects state.
                                    onCheckedChange={() => undefined}
                                />
                                <span className={styles.sourceLabel}>
                                    {t('actions.operate.sources.candidateLabel', {
                                        name: candidate.name,
                                        cageName: candidate.cageName,
                                    })}
                                </span>
                                {isInitial && (
                                    <Badge tone="accent" shape="tag">
                                        {t('actions.operate.sources.initialPill')}
                                    </Badge>
                                )}
                            </li>
                        );
                    })}
                </ul>
            )}
        </section>
    );
}
